import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banknote, FileText, IdCard, ScrollText } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/DataTable'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'

const tk = (tenantId: string, ...parts: unknown[]) => ['tenant', tenantId, 'me', ...parts]

function useMeQuery<T>(path: string, opts: { staleMs?: number } = {}) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', path),
    queryFn: () => api.get<T>(path),
    enabled: Boolean(tenantId),
    staleTime: opts.staleMs ?? 0,
    refetchOnMount: 'always',
  })
}

interface PayslipRow {
  id: string
  pay_run_id: string
  run_number: string
  pay_date: string
  period_start: string
  period_end: string
  gross_minor_units: string
  net_minor_units: string
  currency: string
  delivered_at: string | null
  viewed_at: string | null
}

export function MyPayslipsPage() {
  const { data, isLoading } = useMeQuery<{ data: PayslipRow[]; note?: string }>('/api/me/payslips')
  const rows = data?.data ?? []
  return (
    <Page
      title="My Payslips"
      description="Every pay run that's been posted for you. Click a row to view the full detail."
    >
      {data?.note ? (
        <Card><CardContent className="p-5 text-sm text-muted-foreground">{data.note}</CardContent></Card>
      ) : null}
      <DataTable
        loading={isLoading}
        columns={[
          {
            accessorKey: 'run_number',
            header: 'Pay run',
            cell: ({ row }: { row: { original: PayslipRow } }) => row.original.run_number,
          },
          {
            accessorKey: 'pay_date',
            header: 'Pay date',
            cell: ({ row }: { row: { original: PayslipRow } }) => formatDate(row.original.pay_date),
          },
          {
            accessorKey: 'period_start',
            header: 'Period',
            cell: ({ row }: { row: { original: PayslipRow } }) =>
              `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
          },
          {
            accessorKey: 'gross_minor_units',
            header: 'Gross',
            cell: ({ row }: { row: { original: PayslipRow } }) =>
              formatPayrollMoney(row.original.gross_minor_units, row.original.currency),
          },
          {
            accessorKey: 'net_minor_units',
            header: 'Net',
            cell: ({ row }: { row: { original: PayslipRow } }) =>
              formatPayrollMoney(row.original.net_minor_units, row.original.currency),
          },
        ] as never}
        rows={rows}
        empty={
          <EmptyState
            icon={ScrollText}
            title="No payslips yet"
            description="Once your employer posts a pay run that includes you, the payslip lands here."
          />
        }
      />
    </Page>
  )
}

interface TaxFormRow {
  id: string
  filing_type: string
  tax_year: number
  period_start: string
  period_end: string
  status: string
  filed_at: string | null
}

export function MyTaxFormsPage() {
  const { data, isLoading } = useMeQuery<{ data: TaxFormRow[] }>('/api/me/tax-forms')
  const rows = data?.data ?? []
  return (
    <Page
      title="My Tax Forms"
      description="W-2 and 1099-NEC forms your employer has generated for you. Available immediately after the employer signs them; PDF downloads ship with the S3 docs bucket (infra)."
    >
      <DataTable
        loading={isLoading}
        columns={[
          {
            accessorKey: 'filing_type',
            header: 'Form',
            cell: ({ row }: { row: { original: TaxFormRow } }) =>
              row.original.filing_type === 'us_w2' ? 'W-2'
                : row.original.filing_type === 'us_1099_nec' ? '1099-NEC'
                : row.original.filing_type === 'us_1099_misc' ? '1099-MISC'
                : row.original.filing_type,
          },
          { accessorKey: 'tax_year', header: 'Year' },
          {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }: { row: { original: TaxFormRow } }) => (
              <Badge variant={row.original.status === 'filed' ? 'success' : row.original.status === 'signed' ? 'info' : 'outline'}>
                {row.original.status}
              </Badge>
            ),
          },
          {
            accessorKey: 'filed_at',
            header: 'Filed',
            cell: ({ row }: { row: { original: TaxFormRow } }) =>
              row.original.filed_at ? formatDate(row.original.filed_at) : '-',
          },
        ] as never}
        rows={rows}
        empty={
          <EmptyState
            icon={IdCard}
            title="No tax forms available yet"
            description="W-2s land here in January once your employer generates and signs them."
          />
        }
      />
    </Page>
  )
}

interface W4Row {
  id: string
  filing_status: string | null
  multiple_jobs: boolean
  dependents_count: number
  dependents_credit_minor_units: string
  other_income_minor_units: string
  deductions_minor_units: string
  additional_withholding_minor_units: string
  exempt: boolean
  effective_from: string
  signed_at: string | null
  updated_at: string
}

export function MyW4Page() {
  const { toast } = useToast()
  const { data, isLoading, refetch } = useMeQuery<{ data: W4Row | null }>('/api/me/w4')
  const w4 = data?.data
  const [form, setForm] = useState({
    filing_status: 'single',
    multiple_jobs: false,
    dependents_count: '0',
    additional_withholding_minor_units: '0',
    exempt: false,
  })
  useEffect(() => {
    if (w4) {
      setForm({
        filing_status: w4.filing_status ?? 'single',
        multiple_jobs: w4.multiple_jobs,
        dependents_count: String(w4.dependents_count ?? 0),
        additional_withholding_minor_units: String(w4.additional_withholding_minor_units ?? 0),
        exempt: w4.exempt,
      })
    }
  }, [w4])
  async function save() {
    try {
      await api.put('/api/me/w4', {
        ...form,
        dependents_count: Number(form.dependents_count) || 0,
        additional_withholding_minor_units: Number(form.additional_withholding_minor_units) || 0,
      })
      toast({ title: 'W-4 updated' })
      refetch()
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }
  return (
    <Page
      title="My W-4 (Federal Withholding)"
      description="Update your federal income tax withholding election. Changes apply to the next pay run."
      actions={<Button onClick={save}>Save W-4</Button>}
    >
      <Card>
        <CardContent className="space-y-4 p-6">
          {isLoading ? <LoadingSpinner /> : (
            <>
              <FormField
                label="Filing status"
                control={
                  <select value={form.filing_status} onChange={(e) => setForm({ ...form, filing_status: e.target.value })}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="single">Single (or married filing separately)</option>
                    <option value="married_filing_jointly">Married filing jointly</option>
                    <option value="head_of_household">Head of household</option>
                    <option value="qualifying_widow">Qualifying widow(er)</option>
                  </select>
                }
              />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.multiple_jobs} onChange={(e) => setForm({ ...form, multiple_jobs: e.target.checked })} />
                Multiple jobs (or working spouse) - IRS Step 2 checkbox
              </label>
              <FormField
                label="Number of dependents"
                inputMode="numeric"
                value={form.dependents_count}
                onChange={(e) => setForm({ ...form, dependents_count: e.target.value.replace(/\D/g, '') })}
              />
              <FormField
                label="Extra federal withholding per pay period (cents)"
                hint="Optional. Specify in minor units (cents). $50 → 5000."
                inputMode="numeric"
                value={form.additional_withholding_minor_units}
                onChange={(e) => setForm({ ...form, additional_withholding_minor_units: e.target.value.replace(/\D/g, '') })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.exempt} onChange={(e) => setForm({ ...form, exempt: e.target.checked })} />
                Claim exemption from withholding (rare - must qualify under IRS rules)
              </label>
              {w4 ? (
                <p className="text-xs text-muted-foreground">
                  Last updated {formatDate(w4.updated_at)}. Signed {w4.signed_at ? formatDate(w4.signed_at) : '-'}.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">No W-4 on file yet. Saving creates your first record.</p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}

interface BankRow {
  id: string
  label: string
  routing_number_last4: string
  account_number_last4: string
  account_type: string
  split_kind: string
  is_primary: boolean
  prenote_status: string
  verified_at: string | null
  added_at: string
}

export function MyDirectDepositPage() {
  const { toast } = useToast()
  const { data, isLoading, refetch } = useMeQuery<{ data: BankRow[] }>('/api/me/direct-deposit')
  const rows = data?.data ?? []
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    label: 'Primary', routing_number: '', account_number: '',
    account_type: 'checking', split_kind: 'remainder', is_primary: true,
  })
  async function save() {
    try {
      await api.post('/api/me/direct-deposit', form)
      toast({ title: 'Bank account added', description: 'Prenote queued for verification.' })
      setOpen(false)
      setForm({ ...form, routing_number: '', account_number: '' })
      refetch()
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }
  return (
    <Page
      title="My Direct Deposit"
      description="Bank accounts where your net pay is deposited. Changes require a 3-day prenote verification before they take effect."
      actions={<Button onClick={() => setOpen(true)}>Add account</Button>}
    >
      <DataTable
        loading={isLoading}
        columns={[
          { accessorKey: 'label', header: 'Label' },
          {
            accessorKey: 'routing_number_last4',
            header: 'Routing',
            cell: ({ row }: { row: { original: BankRow } }) => `••• ${row.original.routing_number_last4}`,
          },
          {
            accessorKey: 'account_number_last4',
            header: 'Account',
            cell: ({ row }: { row: { original: BankRow } }) => `••• ${row.original.account_number_last4}`,
          },
          { accessorKey: 'account_type', header: 'Type' },
          { accessorKey: 'split_kind', header: 'Split' },
          {
            accessorKey: 'is_primary',
            header: 'Primary',
            cell: ({ row }: { row: { original: BankRow } }) =>
              row.original.is_primary ? <Badge variant="success">Yes</Badge> : <Badge variant="outline">No</Badge>,
          },
          {
            accessorKey: 'prenote_status',
            header: 'Prenote',
            cell: ({ row }: { row: { original: BankRow } }) => (
              <Badge variant={row.original.prenote_status === 'verified' ? 'success' : 'warning'}>
                {row.original.prenote_status}
              </Badge>
            ),
          },
        ] as never}
        rows={rows}
        empty={
          <EmptyState
            icon={Banknote}
            title="No direct deposit account yet"
            description="Add a bank account. Routing + account numbers are encrypted at rest; only last-4 displays."
            action={<Button onClick={() => setOpen(true)}>Add account</Button>}
          />
        }
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Add direct-deposit account"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField label="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <FormField
            label="Routing number (9 digits)"
            inputMode="numeric"
            maxLength={9}
            value={form.routing_number}
            onChange={(e) => setForm({ ...form, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
          />
          <FormField
            label="Account number"
            inputMode="numeric"
            value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, '') })}
          />
          <FormField
            label="Account type"
            control={
              <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            }
          />
        </div>
      </Modal>
    </Page>
  )
}

export function MyAccountIndexPage() {
  const navigate = useNavigate()
  return (
    <Page
      title="My Account"
      description="Your employee self-service surface - payslips, tax forms, W-4, direct deposit."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { title: 'My Payslips', icon: ScrollText, path: '/account/my-payslips' },
          { title: 'My Tax Forms', icon: IdCard, path: '/account/my-tax-forms' },
          { title: 'My W-4', icon: FileText, path: '/account/my-w4' },
          { title: 'My Direct Deposit', icon: Banknote, path: '/account/my-direct-deposit' },
        ].map(({ title, icon: Icon, path }) => (
          <Card key={path} className="cursor-pointer hover:bg-accent/40" onClick={() => navigate(path)}>
            <CardContent className="flex items-center gap-3 p-5">
              <Icon className="size-6 text-primary" />
              <span className="text-sm font-medium">{title}</span>
            </CardContent>
          </Card>
        ))}
      </div>
    </Page>
  )
}
